import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../../config';
import { useOrganization } from '../../../context/OrganizationContext';
import { formatDistanceToNow } from 'date-fns';

import {
  ArrowCounterClockwise,
  CaretDown,
  CaretRight,
  Check,
  CircleNotch,
  Clock,
  ClockCounterClockwise,
  DotsThreeVertical,
  Eye,
  FloppyDisk,
  GitBranch,
  Rocket,
  User,
  Warning,
  X
} from '@phosphor-icons/react';
/**
 * VersionHistoryPanel - Agent Configuration Version Control
 * 
 * Features:
 * - View version history timeline
 * - Save new versions with notes
 * - Revert to previous versions
 * - Compare versions (coming soon)
 * - Mark versions as deployed
 */
const VersionHistoryPanel = ({ 
  agentId, 
  agentConfig, 
  onRevert,
  onVersionSaved,
  isCollapsed = false,
  onToggle 
}) => {
  const { activeOrganization } = useOrganization();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);

  // Fetch versions
  const fetchVersions = useCallback(async () => {
    if (!agentId || !activeOrganization?.organization_id) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/agents/${agentId}/versions?limit=20`,
        {
          credentials: 'include',
          headers: { 
            'X-Organization-Id': activeOrganization.organization_id 
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setVersions(data.versions || []);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId, activeOrganization]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  // Save new version
  const handleSaveVersion = async () => {
    if (!agentId || !activeOrganization?.organization_id) return;
    
    setSaving(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/agents/${agentId}/versions`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': activeOrganization.organization_id
          },
          body: JSON.stringify({
            agent_id: agentId,
            config: agentConfig,
            change_summary: changeSummary || `Version saved`,
            is_auto_save: false
          })
        }
      );
      
      if (response.ok) {
        const newVersion = await response.json();
        toast.success(`Version ${newVersion.version_number} saved`);
        setShowSaveModal(false);
        setChangeSummary('');
        fetchVersions();
        onVersionSaved?.(newVersion);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save version');
      }
    } catch (error) {
      console.error('Error saving version:', error);
      toast.error('Failed to save version');
    } finally {
      setSaving(false);
    }
  };

  // Revert to version
  const handleRevert = async () => {
    if (!selectedVersion || !agentId || !activeOrganization?.organization_id) return;
    
    setReverting(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/agents/${agentId}/versions/revert`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': activeOrganization.organization_id
          },
          body: JSON.stringify({
            version_id: selectedVersion.version_id,
            create_backup: true
          })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Reverted to version ${selectedVersion.version_number}`);
        setShowRevertConfirm(false);
        setSelectedVersion(null);
        fetchVersions();
        onRevert?.(result);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to revert');
      }
    } catch (error) {
      console.error('Error reverting:', error);
      toast.error('Failed to revert version');
    } finally {
      setReverting(false);
    }
  };

  // Mark as deployed
  const handleMarkDeployed = async (version) => {
    if (!agentId || !activeOrganization?.organization_id) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/agents/${agentId}/versions/${version.version_id}/deploy`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-Organization-Id': activeOrganization.organization_id
          }
        }
      );
      
      if (response.ok) {
        toast.success(`Version ${version.version_number} marked as deployed`);
        fetchVersions();
      }
    } catch (error) {
      console.error('Error marking deployed:', error);
    }
  };

  // Format time
  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  if (isCollapsed) {
    return (
      <button
        onClick={onToggle}
        className="w-full p-3 flex items-center gap-2 text-slate-400 hover:text-white
          hover:bg-white/5 transition-colors border-b border-white/5"
        data-testid="version-history-toggle"
      >
        <ClockCounterClockwise className="w-4 h-4" />
        <span className="text-sm font-medium">Version History</span>
        <CaretRight className="w-4 h-4 ml-auto" />
      </button>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[#0d0e12]" data-testid="version-history-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise className="w-4 h-4 text-indigo-400" />
          <h3 className="text-sm font-semibold text-white">Version History</h3>
          {versions.length > 0 && (
            <span className="text-xs text-slate-500">
              ({versions.length})
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSaveModal(true)}
            disabled={!agentId}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium
              bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 
              disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="save-version-btn"
          >
            <FloppyDisk className="w-3.5 h-3.5" />
            Save Version
          </button>
          
          {onToggle && (
            <button
              onClick={onToggle}
              className="p-1.5 text-slate-500 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <CircleNotch className="w-5 h-5 text-indigo-400 animate-spin" />
          </div>
        ) : versions.length === 0 ? (
          <div className="p-6 text-center">
            <GitBranch className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-400 mb-1">No versions saved yet</p>
            <p className="text-xs text-slate-500">
              Save your first version to start tracking changes
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {versions.map((version, index) => (
              <VersionItem
                key={version.version_id}
                version={version}
                isLatest={index === 0}
                onRevert={() => {
                  setSelectedVersion(version);
                  setShowRevertConfirm(true);
                }}
                onMarkDeployed={() => handleMarkDeployed(version)}
                formatTime={formatTime}
              />
            ))}
          </div>
        )}
      </div>

      {/* Save Version Modal */}
      <AnimatePresence>
        {showSaveModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowSaveModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1a1b23] rounded-xl border border-white/10 w-full max-w-md p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FloppyDisk className="w-5 h-5 text-indigo-400" />
                Save Version
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    What changed? (optional)
                  </label>
                  <input
                    type="text"
                    value={changeSummary}
                    onChange={(e) => setChangeSummary(e.target.value)}
                    placeholder="e.g., Updated system prompt, added new capability"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10
                      text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50"
                    data-testid="change-summary-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setShowSaveModal(false)}
                    className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveVersion}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                      bg-indigo-600 text-white hover:bg-indigo-500 
                      disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="confirm-save-btn"
                  >
                    {saving ? (
                      <CircleNotch className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {saving ? 'Saving...' : 'Save Version'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Revert Confirmation Modal */}
      <AnimatePresence>
        {showRevertConfirm && selectedVersion && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowRevertConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1a1b23] rounded-xl border border-white/10 w-full max-w-md p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                <ArrowCounterClockwise className="w-5 h-5 text-amber-400" />
                Revert to Version {selectedVersion.version_number}?
              </h3>
              
              <p className="text-sm text-slate-400 mb-4">
                This will restore the agent configuration from{' '}
                <span className="text-white">
                  {formatTime(selectedVersion.created_at)}
                </span>
                . A backup of the current configuration will be saved automatically.
              </p>

              {selectedVersion.change_summary && (
                <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-slate-500 mb-1">Version notes:</p>
                  <p className="text-sm text-white">{selectedVersion.change_summary}</p>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowRevertConfirm(false)}
                  className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevert}
                  disabled={reverting}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                    bg-amber-600 text-white hover:bg-amber-500 
                    disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="confirm-revert-btn"
                >
                  {reverting ? (
                    <CircleNotch className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowCounterClockwise className="w-4 h-4" />
                  )}
                  {reverting ? 'Reverting...' : 'Revert'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Individual version item component
const VersionItem = ({ version, isLatest, onRevert, onMarkDeployed, formatTime }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div 
      className="p-4 hover:bg-white/5 transition-colors group"
      data-testid={`version-item-${version.version_number}`}
    >
      <div className="flex items-start gap-3">
        {/* Version indicator */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
          ${version.is_deployed 
            ? 'bg-green-500/20 text-green-400 ring-2 ring-green-500/30' 
            : isLatest 
              ? 'bg-indigo-500/20 text-indigo-400' 
              : 'bg-white/10 text-slate-400'
          }`}>
          v{version.version_number}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-white">
              {version.change_summary || `Version ${version.version_number}`}
            </span>
            {version.is_deployed && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium
                bg-green-500/20 text-green-400">
                <Rocket className="w-3 h-3" />
                Deployed
              </span>
            )}
            {isLatest && !version.is_deployed && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium
                bg-indigo-500/20 text-indigo-400">
                Latest
              </span>
            )}
            {version.is_auto_save && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium
                bg-slate-500/20 text-slate-400">
                Auto
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTime(version.created_at)}
            </span>
            {version.created_by_name && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {version.created_by_name}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 text-slate-500 hover:text-white opacity-0 group-hover:opacity-100
              transition-all rounded-lg hover:bg-white/10"
          >
            <DotsThreeVertical className="w-4 h-4" />
          </button>

          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute right-0 top-8 w-40 bg-[#1a1b23] rounded-lg border border-white/10
                  shadow-xl z-10 overflow-hidden"
              >
                <button
                  onClick={() => {
                    onRevert();
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-white/10
                    flex items-center gap-2 transition-colors"
                  data-testid={`revert-to-${version.version_number}`}
                >
                  <ArrowCounterClockwise className="w-4 h-4" />
                  Revert to this
                </button>
                {!version.is_deployed && (
                  <button
                    onClick={() => {
                      onMarkDeployed();
                      setShowMenu(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-white/10
                      flex items-center gap-2 transition-colors"
                  >
                    <Rocket className="w-4 h-4" />
                    Mark as Deployed
                  </button>
                )}
                <button
                  onClick={() => setShowMenu(false)}
                  className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-white/10
                    flex items-center gap-2 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  View Details
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default VersionHistoryPanel;
